import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    Button,
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-hot-toast';

function BookingForgetPassword() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Send OTP, 2: Verify OTP, 3: Reset Password
    const [loading, setLoading] = useState(false);
    
    // States
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);

    const token = localStorage.getItem('vedhvidhi-admin-token');

    const handleSendOTP = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BASEURL}/admin/chat-password/forgot`, 
                {}, 
                { headers: { Authorization: token } }
            );

            if (response.data.status) {
                toast.success(response.data.message || 'OTP sent successfully');
                setStep(2);
            } else {
                toast.error(response.data.message || 'Failed to send OTP');
            }
        } catch (error) {
            console.error('Send OTP error:', error);
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp) {
            toast.error('Please enter OTP');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BASEURL}/admin/chat-password/verify-otp`, 
                { otp: Number(otp) }, 
                { headers: { Authorization: token } }
            );

            if (response.data.status) {
                toast.success(response.data.message || 'OTP verified successfully');
                setStep(3);
            } else {
                toast.error(response.data.message || 'Invalid OTP');
            }
        } catch (error) {
            console.error('Verify OTP error:', error);
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || !confirmPassword) {
            toast.error('Please fill all fields');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BASEURL}/admin/chat-password/reset`, 
                { 
                    newPassword: newPassword,
                    confirmPassword: confirmPassword
                }, 
                { headers: { Authorization: token } }
            );

            if (response.data.status) {
                toast.success(response.data.message || 'Password reset successfully');
                navigate('/booking-password');
            } else {
                toast.error(response.data.message || 'Failed to reset password');
            }
        } catch (error) {
            console.error('Reset error:', error);
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="body d-flex py-1">
            <div className="container-xxl">
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        marginBottom: "10px",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "monospace",
                        fontSize: "18px",
                        color: "#007bff",
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        padding: "10px 20px",
                    }}
                >
                    <span style={{ marginRight: "8px" }}>&lt;</span>
                    <span style={{ position: "relative", display: "inline-block" }}>
                        Back
                        <span
                            style={{
                                content: "''",
                                position: "absolute",
                                left: 0,
                                bottom: -2,
                                width: "100%",
                                height: "1px",
                                borderBottom: "2px solid #007bff",
                            }}
                        ></span>
                    </span>
                </button>
                <PageHeader1 pagetitle="Forget Booking Password" />

                <div className="row g-4 mt-2 justify-content-center">
                    <div className="col-12">
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                            <CardContent className="p-4">
                                {step === 1 && (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                                            Forget Password
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 4, color: '#666' }}>
                                            Click the button below to receive an OTP on your registered email.
                                        </Typography>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={handleSendOTP}
                                            disabled={loading}
                                            sx={{
                                                py: 1.5,
                                                backgroundColor: '#7258db',
                                                '&:hover': { backgroundColor: '#5e44c4' },
                                                borderRadius: '8px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : 'SEND OTP'}
                                        </Button>
                                    </Box>
                                )}

                                {step === 2 && (
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                                            Verify OTP
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 4, color: '#666' }}>
                                            Please enter the OTP sent to your email.
                                        </Typography>
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            label="Enter OTP"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            variant="outlined"
                                            sx={{ mb: 3 }}
                                        />
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={handleVerifyOTP}
                                            disabled={loading}
                                            sx={{
                                                py: 1.5,
                                                backgroundColor: '#7258db',
                                                '&:hover': { backgroundColor: '#5e44c4' },
                                                borderRadius: '8px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : 'VERIFY OTP'}
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="text"
                                            onClick={() => setStep(1)}
                                            sx={{ mt: 1, color: '#666' }}
                                        >
                                            Back
                                        </Button>
                                    </Box>
                                )}

                                {step === 3 && (
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                                            Reset Password
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 4, color: '#666' }}>
                                            Enter your new booking password.
                                        </Typography>
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            label="New Password"
                                            type={showNewPass ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            variant="outlined"
                                            sx={{ mb: 2 }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowNewPass(!showNewPass)} edge="end">
                                                            {showNewPass ? <i className="icofont-eye-blocked" /> : <i className="icofont-eye" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            label="Confirm New Password"
                                            type={showConfirmPass ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            variant="outlined"
                                            sx={{ mb: 3 }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowConfirmPass(!showConfirmPass)} edge="end">
                                                            {showConfirmPass ? <i className="icofont-eye-blocked" /> : <i className="icofont-eye" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={handleResetPassword}
                                            disabled={loading}
                                            sx={{
                                                py: 1.5,
                                                backgroundColor: '#7258db',
                                                '&:hover': { backgroundColor: '#5e44c4' },
                                                borderRadius: '8px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : 'RESET PASSWORD'}
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BookingForgetPassword;
